<template>
  <div v-if="campaign">
    <vx-card class="mb-4">
      <div class="flex justify-between items-start flex-wrap gap-2">
        <div>
          <h4 class="mb-0">{{ campaign.name }}</h4>
          <small class="text-muted">
            {{ _.get(campaign, 'legal_entity.name') }} · {{ windowLabel }}
          </small>
        </div>
        <div class="flex items-center gap-2">
          <vs-chip transparent :color="statusColor" class="text-base">
            {{ $t(`campaign.status_.${campaign.status}`) }}
          </vs-chip>
          <b-button v-permission="'campaigns.approve'" v-if="campaign.status === 'PA'" size="sm" variant="success" @click="act('approve')">
            {{ $t('campaign.accept') }}
          </b-button>
          <b-button v-permission="'campaigns.approve'" v-if="campaign.status === 'PA'" size="sm" variant="outline-danger" @click="act('reject')">
            {{ $t('campaign.refuse') }}
          </b-button>
          <b-button v-permission="'campaigns.pause'" v-if="campaign.status === 'AC'" size="sm" variant="outline-warning" @click="act('pause')">
            {{ $t('campaign.pause') }}
          </b-button>
          <b-button v-permission="'campaigns.pause'" v-if="campaign.status === 'PU'" size="sm" variant="outline-success" @click="act('resume')">
            {{ $t('campaign.resume') }}
          </b-button>
          <b-button v-permission="'campaigns.cancel'" v-if="!['CA','EN'].includes(campaign.status)" size="sm" variant="outline-danger" @click="act('cancel')">
            {{ $t('campaign.cancel') }}
          </b-button>
        </div>
      </div>

      <div class="stats-row mt-3">
        <div class="stat">
          <span class="stat-label">{{ $t('campaign.discount') }}</span>
          <span class="stat-value">{{ amountLabel(campaign.discount_type, campaign.discount_amount) }}</span>
        </div>
        <div class="stat">
          <span class="stat-label">{{ $t('campaign.commission') }}</span>
          <span class="stat-value">{{ amountLabel(campaign.commission_type, campaign.commission_amount) }}</span>
        </div>
        <div class="stat">
          <span class="stat-label">{{ $t('campaign.budget') }}</span>
          <span class="stat-value">
            <template v-if="campaign.budget_cap_cents">
              {{ money(campaign.budget_spent_cents) }} / {{ money(campaign.budget_cap_cents) }}
            </template>
            <template v-else>{{ $t('campaign.no_budget_cap') }}</template>
          </span>
        </div>
        <div class="stat">
          <span class="stat-label">{{ $t('campaign.participants') }}</span>
          <span class="stat-value">{{ activeParticipations.length }}</span>
        </div>
      </div>
    </vx-card>

    <b-tabs>
      <b-tab :title="$t('campaign.tab_participations')" active>
        <vx-card class="mt-2">
          <div v-if="!(campaign.participations || []).length" class="text-muted">
            {{ $t('campaign.no_participants') }}
          </div>
          <table v-else class="w-full detail-table">
            <thead>
              <tr>
                <th>{{ $t('influencer.name') }}</th>
                <th>{{ $t('campaign.coupon') }}</th>
                <th>{{ $t('campaign.link') }}</th>
                <th>{{ $t('campaign.status') }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in campaign.participations" :key="p.id">
                <td>
                  {{ _.get(p, 'influencer.name') }}
                  <small class="text-muted d-block">
                    <span v-for="s in _.get(p, 'influencer.social_profiles', [])" :key="s.id" class="mr-1">
                      {{ s.network_label }} @{{ s.handle }}
                    </span>
                  </small>
                </td>
                <td><code v-if="p.coupon_code">{{ p.coupon_code }}</code><span v-else class="text-muted">—</span></td>
                <td>
                  <code v-if="p.tracking_slug">/r/{{ p.tracking_slug }}</code>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>
                  <vs-chip transparent :color="participationColor(p.status)">
                    {{ $t(`campaign.participation_status_.${p.status}`) }}
                  </vs-chip>
                </td>
                <td class="text-right">
                  <b-button v-permission="'campaign_participations.approve'" v-if="p.status === 'AP'" size="sm" variant="success" class="mr-1" @click="participationAct(p, 'approve')">
                    {{ $t('influencer.approve') }}
                  </b-button>
                  <b-button v-permission="'campaign_participations.approve'" v-if="p.status === 'AP'" size="sm" variant="outline-danger" @click="participationAct(p, 'reject')">
                    {{ $t('influencer.reject') }}
                  </b-button>
                  <b-button v-permission="'campaign_participations.approve'" v-if="p.status === 'AC'" size="sm" variant="outline-warning" @click="participationAct(p, 'pause')">
                    {{ $t('campaign.pause') }}
                  </b-button>
                  <b-button v-permission="'campaign_participations.approve'" v-if="p.status === 'AC'" size="sm" variant="outline-danger" class="ml-1" @click="participationAct(p, 'block')">
                    {{ $t('campaign.block') }}
                  </b-button>
                </td>
              </tr>
            </tbody>
          </table>
        </vx-card>
      </b-tab>

      <b-tab :title="$t('campaign.tab_briefing')">
        <vx-card class="mt-2">
          <h5>{{ $t('campaign.briefing') }}</h5>
          <p class="text-muted">{{ campaign.briefing || '—' }}</p>
          <div class="vx-row">
            <div class="vx-col sm:w-1/2 w-full">
              <h6 class="text-success">{{ $t('campaign.dos') }}</h6>
              <p class="text-muted small">{{ campaign.dos || '—' }}</p>
            </div>
            <div class="vx-col sm:w-1/2 w-full">
              <h6 class="text-danger">{{ $t('campaign.donts') }}</h6>
              <p class="text-muted small">{{ campaign.donts || '—' }}</p>
            </div>
          </div>
          <p class="mb-0">
            <vs-chip v-for="h in (campaign.hashtags || [])" :key="h" transparent color="primary" class="mr-1">{{ h }}</vs-chip>
            <vs-chip v-for="m in (campaign.mentions || [])" :key="m" transparent color="success" class="mr-1">{{ m }}</vs-chip>
            <vs-chip transparent color="warning">{{ campaign.disclosure_tag }}</vs-chip>
          </p>
        </vx-card>
      </b-tab>

      <b-tab :title="$t('campaign.tab_assets')">
        <vx-card class="mt-2">
          <div v-if="!(campaign.assets || []).length" class="text-muted mb-2">{{ $t('campaign.no_assets') }}</div>
          <table v-else class="w-full detail-table mb-2">
            <tbody>
              <tr v-for="a in campaign.assets" :key="a.id">
                <td>
                  {{ a.title }}
                  <vs-chip v-if="a.is_expired" transparent color="danger" class="ml-1">{{ $t('campaign.asset_expired') }}</vs-chip>
                  <small class="text-muted d-block">{{ a.usage_rights }}</small>
                </td>
                <td>
                  <a v-if="a.external_url" :href="a.external_url" target="_blank" rel="noopener noreferrer">{{ $t('campaign.open') }}</a>
                </td>
                <td class="text-right">
                  <feather-icon
                    v-permission="'campaigns.edit'"
                    icon="Trash2Icon"
                    class="h-5 w-5 stroke-current text-danger cursor-pointer"
                    @click="removeAsset(a)"/>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-permission="'campaigns.edit'" class="vx-row items-end">
            <div class="vx-col sm:w-4/12 w-full">
              <b-form-group :label="$t('campaign.asset_title')">
                <b-form-input v-model="newAsset.title" type="text"/>
              </b-form-group>
            </div>
            <div class="vx-col sm:w-2/12 w-full">
              <b-form-group :label="$t('campaign.asset_kind')">
                <b-form-select v-model="newAsset.kind" :options="assetKinds"/>
              </b-form-group>
            </div>
            <div class="vx-col sm:w-3/12 w-full">
              <b-form-group :label="$t('campaign.asset_url')">
                <b-form-input v-model="newAsset.external_url" type="text"/>
              </b-form-group>
            </div>
            <div class="vx-col sm:w-2/12 w-full">
              <b-form-group :label="$t('campaign.asset_expires')">
                <b-form-input v-model="newAsset.expires_at" type="date"/>
              </b-form-group>
            </div>
            <div class="vx-col sm:w-1/12 w-full mb-1">
              <b-button size="sm" variant="primary" @click="addAsset">
                <feather-icon icon="PlusIcon"/>
              </b-button>
            </div>
          </div>
        </vx-card>
      </b-tab>

      <b-tab :title="$t('campaign.tab_timeline')">
        <vx-card class="mt-2">
          <div v-for="e in (campaign.events || [])" :key="e.id" class="timeline-row">
            <div>
              <strong>{{ e.event }}</strong>
              <span v-if="e.description" class="text-muted"> — {{ e.description }}</span>
            </div>
            <small class="text-muted">
              {{ _.get(e, 'actor.name', '—') }} · {{ formatDateTime(e.created_at) }}
            </small>
          </div>
        </vx-card>
      </b-tab>
    </b-tabs>

    <b-form-row class="justify-end mt-3">
      <b-col cols="auto">
        <b-button variant="outline-secondary" @click="goBack">
          <feather-icon icon="CornerUpLeftIcon" class="mr-50"/>
          <span class="align-middle">{{ $t('back') }}</span>
        </b-button>
      </b-col>
    </b-form-row>
  </div>
</template>

<script>
import {
  BButton, BTabs, BTab, BFormGroup, BFormInput, BFormSelect, BFormRow, BCol
} from 'bootstrap-vue'
import CampaignService from '@/services/api/CampaignService'
import CampaignParticipationService from '@/services/api/CampaignParticipationService'

export default {
  components: { BButton, BTabs, BTab, BFormGroup, BFormInput, BFormSelect, BFormRow, BCol },
  props: { id: { type: [Number, String], default: null } },
  data: () => ({
    service: null,
    participationService: null,
    campaign: null,
    newAsset: { title: '', kind: 'IM', external_url: '', expires_at: null },
    assetKinds: [
      { text: 'Imagem', value: 'IM' },
      { text: 'Vídeo', value: 'VI' },
      { text: 'Copy', value: 'CP' },
      { text: 'Logo', value: 'LG' },
      { text: 'Guideline', value: 'GD' }
    ]
  }),
  computed: {
    activeParticipations() {
      return (this.campaign?.participations || []).filter(p => p.status === 'AC')
    },
    statusColor() {
      switch (this.campaign?.status) {
        case 'AC': return 'success'
        case 'PA': case 'PU': return 'warning'
        case 'CA': case 'RJ': return 'danger'
        default: return 'dark'
      }
    },
    windowLabel() {
      const fmt = v => (v ? new Date(v).toLocaleDateString('pt-BR') : '—')
      return `${fmt(this.campaign?.starts_at)} → ${fmt(this.campaign?.ends_at)}`
    }
  },
  methods: {
    money(cents) {
      return (Number(cents || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    amountLabel(type, amount) {
      const n = Number(amount || 0)
      return type === 'VA' ? this.money(n * 100) : `${n}%`
    },
    formatDateTime(v) {
      return v ? new Date(v).toLocaleString('pt-BR') : ''
    },
    participationColor(status) {
      switch (status) {
        case 'AC': return 'success'
        case 'AP': case 'PU': return 'warning'
        case 'RJ': case 'BL': return 'danger'
        default: return 'dark'
      }
    },
    load() {
      this.$vs.loading()
      this.service.detail(this.id).then(
        c => { this.campaign = c },
        () => { this.$vs.loading.close() }
      ).finally(() => { this.$vs.loading.close() })
    },
    act(action) {
      // Recusar, pausar e cancelar registram motivo na timeline; aceitar e
      // retomar não precisam de justificativa.
      const needsReason = ['reject', 'pause', 'cancel'].includes(action)
      const reason = needsReason ? (window.prompt(this.$t('campaign.reason_prompt')) || '') : ''
      if (needsReason && reason === null) return

      this.$vs.loading()
      this.service[action](this.id, reason).then(
        () => {
          this.notifySuccess(this.$vs, this.$t('messages.success'))
          this.load()
        },
        error => this.showErrors(this.$validator, error, this)
      ).finally(() => { this.$vs.loading.close() })
    },
    participationAct(participation, action) {
      const needsReason = ['reject', 'pause', 'block'].includes(action)
      const reason = needsReason ? (window.prompt(this.$t('campaign.reason_prompt')) || '') : ''

      this.$vs.loading()
      this.participationService[action](participation.id, reason).then(
        () => {
          this.notifySuccess(this.$vs, this.$t('messages.success'))
          this.load()
        },
        error => this.showErrors(this.$validator, error, this)
      ).finally(() => { this.$vs.loading.close() })
    },
    addAsset() {
      if (!this.newAsset.title) return
      this.$vs.loading()
      this.service.addAsset(this.id, this.newAsset).then(
        () => {
          this.newAsset = { title: '', kind: 'IM', external_url: '', expires_at: null }
          this.load()
        },
        error => this.showErrors(this.$validator, error, this)
      ).finally(() => { this.$vs.loading.close() })
    },
    removeAsset(asset) {
      this.$vs.loading()
      this.service.removeAsset(this.id, asset.id)
        .then(() => this.load(), error => this.showErrors(this.$validator, error, this))
        .finally(() => { this.$vs.loading.close() })
    },
    goBack() { this.$router.push('/campaigns') }
  },
  beforeMount() {
    this.service = CampaignService.build(this.$vs)
    this.participationService = CampaignParticipationService.build(this.$vs)
  },
  mounted() { this.load() }
}
</script>

<style scoped>
.stats-row { display: flex; flex-wrap: wrap; gap: 2rem; }
.stat { display: flex; flex-direction: column; }
.stat-label { font-size: 0.75rem; opacity: 0.7; }
.stat-value { font-size: 1.05rem; font-weight: 600; }
.detail-table th { font-size: 0.75rem; opacity: 0.7; text-align: left; padding-bottom: 0.35rem; }
.detail-table td { padding: 0.5rem 0; border-top: 1px solid rgba(0,0,0,0.06); vertical-align: top; }
.text-right { text-align: right; }
.timeline-row { padding: 0.4rem 0; border-bottom: 1px solid rgba(0,0,0,0.06); }
.timeline-row:last-child { border-bottom: none; }
</style>
